import { useEffect, useMemo, useState } from "react";

import { Alert, Badge, Button, Card, CardTitle, Chip, ChipGroup, Input, Textarea } from "@realroof/ui";

import { api, canModerate, myLevels, DELETE_REASON_LABEL, REASON_LABEL, STATUS_LABEL, statusText, type ListingDetail, type QueueItem } from "../api";
import { answerSections, rupees, when } from "../format";
import { PendingLocality } from "./PendingLocality";
import { Link, navigate } from "../router";
import { useSession } from "../session";
import { Table, td, th } from "../table";

/** Common reasons, so owners get clear and consistent feedback; staff can edit the text. */
export const REASONS = [
  "Photos do not show this property; add real photos of it.",
  "The price looks wrong; please check the amount and the unit.",
  "Remove phone numbers, links or other contact details from the text and photos.",
  "This ad is a duplicate of one you already posted.",
  "The ad is in the wrong category; post it under the right property type.",
  "The address or locality does not match the map location.",
];

const ACTIONS = "sticky top-0 z-10 -mx-1 flex flex-wrap items-center gap-2 bg-background px-1 py-2";
const TONE = { draft: "neutral", under_review: "warning", live: "success", rejected: "danger", expired: "neutral", deactivated: "danger", deleted: "neutral" } as const;

const ACTION_LABEL: Record<string, string> = {
  approve: "Approved",
  reject: "Sent back for changes",
  take_down: "Taken down",
  approve_changes: "Changes approved",
  reject_changes: "Changes sent back",
  restore: "Restored",
};

export function Review({ id }: { id: string }) {
  const { me } = useSession();
  const [ad, setAd] = useState<ListingDetail | null>(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState<"reject" | "take-down" | "reject-changes" | "return-l1" | null>(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    setAd(null);
    setDone("");
    setMode(null);
    setError("");
    api<ListingDetail>(`/admin/listings/${id}`)
      .then(setAd)
      .catch((err: Error) => setError(err.message));
  }, [id]);

  const places = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = { ...ad?.places };
    const answers = ad?.answers ?? {};
    if (typeof answers.city_id === "string" && ad?.city) out[answers.city_id] = ad.city;
    if (typeof answers.locality_id === "string" && ad?.locality) out[answers.locality_id] = ad.locality;
    return out;
  }, [ad]);

  if (error && !ad) return <Alert tone="error">{error}</Alert>;
  if (!ad) return <p className="text-muted-foreground">Loading…</p>;

  const moderator = canModerate(me?.staff_role) && !!me?.can_approve; // the super admin can take approving away
  // Two-level review: which check is next, and whether this person may make it
  const waitingReview = ad.status === "under_review" || (ad.status === "live" && ad.edit_status === "under_review");
  const stage = ad.check_l1_at ? "l2" : "l1";
  const mine = myLevels(me);
  const madeL1 = stage === "l2" && ad.check_l1_by === me?.id;
  const mayCheck = moderator && mine.includes(stage) && !madeL1;
  const why = !moderator
    ? ""
    : madeL1
      ? "You made the level 1 check on this ad; another person makes level 2."
      : stage === "l1"
        ? "You check at level 2; this needs its level 1 check first."
        : "Level 1 is done; it waits for a level 2 checker.";

  async function act(path: string, body: object, message: string) {
    setBusy(true);
    setError("");
    try {
      setAd(await api<ListingDetail>(`/admin/listings/${id}/${path}`, { method: "POST", body }));
      setDone(message);
      setMode(null);
      setReason("");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function reload(note: string) {
    setDone(note);
    setAd(await api<ListingDetail>(`/admin/listings/${id}`));
  }

  async function next() {
    const queue = await api<QueueItem[]>("/admin/listings?status=under_review&limit=2");
    const following = queue.find((q) => q.id !== id);
    navigate(following ? `/ads/${following.id}` : "/ads");
  }

  const images = ad.media.filter((m) => m.kind === "image");
  const others = ad.media.filter((m) => m.kind !== "image");

  return (
    <article className="flex flex-col gap-4">
      <p>
        <Link to={`/ads?status=${ad.status}`}>← {STATUS_LABEL[ad.status]}</Link>
      </p>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="mb-1 text-2xl font-bold">{ad.title}</h1>
          <p className="font-mono text-xs text-muted-foreground">
            {ad.public_id} · {ad.form_key} · {ad.plan ?? "free"} · submitted {when(ad.submitted_at)}
          </p>
        </div>
        <Badge tone={TONE[ad.status]} className="text-sm">
          {statusText(ad)}
        </Badge>
      </header>

      {done && (
        <Alert tone="success">
          {done}{" "}
          {ad.status !== "under_review" && (
            <Button variant="link" onClick={next}>
              Next ad in the queue →
            </Button>
          )}
        </Alert>
      )}
      {error && <Alert tone="error">{error}</Alert>}

      {waitingReview && (
        <Alert tone={stage === "l2" ? "info" : "warning"} role="note">
          {stage === "l1"
            ? "Waiting for the level 1 check."
            : `Level 1 checked by ${ad.check_l1_name ?? "staff"} on ${when(ad.check_l1_at)}. Waiting for the level 2 check, which publishes it.`}
          {!mayCheck && why && <span className="block text-sm">{why}</span>}
        </Alert>
      )}
      {ad.pending_locality && <PendingLocality place={ad.pending_locality} canDecide={moderator} onDecided={(note) => reload(note)} />}
      {moderator && ad.status === "under_review" && !mode && (
        <div className={ACTIONS}>
          {mayCheck && (
            <>
              <Input className="min-w-52 flex-1" aria-label="Note for the team (optional)" placeholder="Note for the team (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
              <Button
                variant="primary"
                disabled={busy || Boolean(ad.pending_locality)}
                title={ad.pending_locality ? "Decide on the suggested locality first" : undefined}
                onClick={() =>
                  act(
                    "approve",
                    { note: note || null },
                    stage === "l1"
                      ? "Checked at level 1: it now waits for a level 2 check."
                      : ad.plan === "premium"
                        ? "Approved: the ad is live for 3 months."
                        : "Approved: the ad is live for 30 days.",
                  )
                }
              >
                {stage === "l1" ? "Approve (level 1)" : "Approve and publish"}
              </Button>
            </>
          )}
          <Button variant="destructive" disabled={busy} onClick={() => setMode("reject")}>
            Reject…
          </Button>
          {mayCheck && stage === "l2" && (
            <Button variant="outline" disabled={busy} onClick={() => setMode("return-l1")}>
              Return to level 1…
            </Button>
          )}
        </div>
      )}
      {ad.status === "live" && ad.edit_status === "under_review" && ad.pending_answers && (
        <Changes ad={ad} places={places} />
      )}
      {moderator && ad.status === "live" && ad.edit_status === "under_review" && !mode && (
        <div className={ACTIONS}>
          {mayCheck && (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => act("approve-changes", {}, stage === "l1" ? "Changes checked at level 1: they now wait for a level 2 check." : "Changes approved: the ad now shows them.")}
            >
              {stage === "l1" ? "Approve changes (level 1)" : "Approve and publish changes"}
            </Button>
          )}
          <Button variant="destructive" disabled={busy} onClick={() => setMode("reject-changes")}>
            Ask for changes…
          </Button>
          {mayCheck && stage === "l2" && (
            <Button variant="outline" disabled={busy} onClick={() => setMode("return-l1")}>
              Return to level 1…
            </Button>
          )}
        </div>
      )}
      {(ad.status === "deleted" || (ad.status === "deactivated" && ad.deactivated_by === "owner")) && (
        <Alert tone="info" role="note">
          {ad.status === "deleted" ? "Deleted" : "Deactivated"} by the owner {when(ad.deleted_at)} ({DELETE_REASON_LABEL[ad.delete_reason ?? ""] ?? ad.delete_reason})
          {ad.delete_note ? `: “${ad.delete_note}”` : ""}. It was {STATUS_LABEL[ad.deleted_from ?? "live"].toLowerCase()}.{" "}
          {moderator && (
            <Button variant="link" disabled={busy} onClick={() => act("restore", {}, "Restored.")}>
              Restore
            </Button>
          )}
        </Alert>
      )}
      {moderator && ad.status === "live" && !mode && (
        <div className={ACTIONS}>
          <span className="text-sm text-muted-foreground">Live until {when(ad.expires_at)}</span>
          <Button variant="destructive" onClick={() => setMode("take-down")}>
            Take down…
          </Button>
        </div>
      )}
      {mode && (
        <Card
          role="group"
          aria-label={mode === "reject" ? "Reject this ad" : mode === "reject-changes" ? "Send the changes back" : mode === "return-l1" ? "Return to level 1" : "Take this ad down"}
          className="border-destructive/40"
        >
          <strong>{mode === "take-down" ? "Why is this ad being taken down?" : mode === "return-l1" ? "What should level 1 look at again?" : "What should the owner change?"}</strong>
          {mode === "reject" && (
            <ChipGroup>
              {REASONS.map((r) => (
                <Chip key={r} aria-pressed={reason === r} onClick={() => setReason(r)}>
                  {r}
                </Chip>
              ))}
            </ChipGroup>
          )}
          <Textarea
            rows={3}
            aria-label={mode === "return-l1" ? "Note for level 1" : "Reason the owner will see"}
            placeholder={mode === "return-l1" ? "Only the team sees this" : "The owner sees this"}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <span className="text-sm text-muted-foreground">{reason.trim().length < 10 ? "At least 10 characters." : `${reason.trim().length} / 300`}</span>
          <div className="flex flex-wrap items-center gap-4">
            <Button
              variant="destructive"
              disabled={busy || reason.trim().length < 10}
              onClick={() =>
                act(
                  mode,
                  { reason: reason.trim() },
                  mode === "reject"
                    ? "Sent back: the owner sees your reason and can fix the ad."
                    : mode === "reject-changes"
                      ? "Changes sent back: the ad stays live as it was."
                      : mode === "return-l1"
                        ? "Returned to level 1 with your note."
                        : "Taken down: the ad is no longer shown.",
                )
              }
            >
              {mode === "take-down" ? "Take down" : mode === "return-l1" ? "Return to level 1" : "Send back to owner"}
            </Button>
            <Button variant="link" onClick={() => setMode(null)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}
      {ad.rejection_reason && ad.status !== "under_review" && (
        <Alert tone="warning" role="note">
          Reason given: {ad.rejection_reason}
        </Alert>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-[1fr_300px]">
        <div className="flex min-w-0 flex-col gap-4">
          <Card>
            <CardTitle className="text-base">Photos ({images.length})</CardTitle>
            {images.length === 0 ? (
              <p className="text-muted-foreground">No photos.</p>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2">
                {images.map((m) => (
                  <a key={m.id} href={m.url} target="_blank" rel="noreferrer">
                    <img src={m.url} alt={`${m.field} photo`} loading="lazy" className="aspect-[4/3] w-full rounded-md bg-muted object-cover" />
                  </a>
                ))}
              </div>
            )}
            {others.map((m) => (
              <p key={m.id}>
                <a href={m.url} target="_blank" rel="noreferrer">
                  {m.kind} ({m.field}, {Math.max(1, Math.round(m.size_bytes / 1024))} KB)
                </a>
              </p>
            ))}
          </Card>
          {answerSections(ad.form_key, ad.answers, places).map((sec) => (
            <Card key={sec.title}>
              <CardTitle className="text-base">{sec.title}</CardTitle>
              <dl className="m-0 grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-x-4 gap-y-2.5">
                {sec.rows.map((r) => (
                  <div key={r.label}>
                    <dt className="text-xs text-muted-foreground">{r.label}</dt>
                    <dd className="m-0 [overflow-wrap:anywhere]">{r.value}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          ))}
        </div>
        <aside className="flex flex-col gap-4">
          <Card>
            <CardTitle className="text-base">Owner</CardTitle>
            <p>
              <strong>{ad.owner.full_name || "No name"}</strong>
              <br />
              {ad.owner.mobile}
              <br />
              {ad.owner.email}
            </p>
            <p className="text-sm text-muted-foreground">
              {ad.owner.role} · member since {when(ad.owner.created_at)}
              <br />
              {ad.owner.ads_total} ads · {ad.owner.ads_live} live · {ad.owner.ads_rejected} sent back
            </p>
          </Card>
          <Card>
            <CardTitle className="text-base">Price</CardTitle>
            <p className="font-display text-xl font-bold">{rupees(ad.price)}</p>
            {ad.status !== "under_review" && (
              <p className="text-sm text-muted-foreground">
                {ad.views.toLocaleString("en-IN")} views · number shown {ad.contacts.toLocaleString("en-IN")} times
              </p>
            )}
          </Card>
          {ad.reports.length > 0 && (
            <Card className={ad.reports.some((r) => r.status === "open") ? "border-destructive/50" : undefined}>
              <CardTitle className="text-base">Reports ({ad.reports.filter((r) => r.status === "open").length} open)</CardTitle>
              <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                {ad.reports.map((r) => (
                  <li key={r.id}>
                    <strong>{REASON_LABEL[r.reason] ?? r.reason}</strong>
                    <span className="text-xs text-muted-foreground"> · {r.status}</span>
                    {r.details && <div className="text-sm">“{r.details}”</div>}
                    <div className="text-xs text-muted-foreground">
                      {when(r.created_at)} · {r.reporter}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <Card>
            <CardTitle className="text-base">History</CardTitle>
            {ad.history.length === 0 ? (
              <p className="text-muted-foreground">No staff action yet.</p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                {ad.history.map((h, i) => (
                  <li key={i}>
                    <strong>{ACTION_LABEL[h.action] ?? h.action}</strong> by {h.staff ?? "system"}
                    <div className="text-xs text-muted-foreground">{when(h.created_at)}</div>
                    {typeof h.detail.reason === "string" && <div>{h.detail.reason}</div>}
                    {typeof h.detail.note === "string" && <div>Note: {h.detail.note}</div>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </aside>
      </div>
    </article>
  );
}

/** The owner's changes to a live ad, field by field: what is live now, and what it becomes. */
function Changes({ ad, places }: { ad: ListingDetail; places: Record<string, string> }) {
  const flat = (answers: Record<string, unknown>) =>
    new Map(answerSections(ad.form_key, answers, places).flatMap((s) => s.rows.map((r) => [`${s.title} · ${r.label}`, r.value] as const)));
  const before = flat(ad.answers);
  const after = flat(ad.pending_answers ?? {});
  const keys = [...new Set([...before.keys(), ...after.keys()])].filter((k) => before.get(k) !== after.get(k));
  return (
    <Card className="border-premium/50" aria-label="Changes waiting for review">
      <CardTitle className="text-base">Changes waiting for review (sent {when(ad.edit_submitted_at)})</CardTitle>
      <p className="m-0 text-sm text-muted-foreground">The ad stays live as it is until you approve. Photos show the changed version below.</p>
      {(ad.photo_changes ?? []).length > 0 && (
        <ul className="m-0 flex list-disc flex-col gap-1 pl-5" aria-label="Photo changes">
          {ad.photo_changes!.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      )}
      {keys.length === 0 ? (
        !ad.photo_changes?.length && <p className="m-0">Only photos or files changed.</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <th className={th}>Answer</th>
              <th className={th}>Live now</th>
              <th className={th}>Changed to</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k}>
                <td className={td}>{k}</td>
                <td className={`${td} text-muted-foreground line-through`}>{before.get(k) ?? "–"}</td>
                <td className={`${td} font-semibold`}>{after.get(k) ?? "(removed)"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
